import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import {
  buildItineraryConfigDoc,
  fetchItineraryConfig,
  saveItineraryConfig,
  type ItineraryConfigDoc,
} from "../../Khushi/khushiAccess";
import { KHUSHI_ITINERARY } from "../../Khushi/khushiData";
import { KHUSHI_COLORS, KHUSHI_FONTS } from "../../Khushi/theme";

const { gold, goldSoft, ink, maroon } = KHUSHI_COLORS;
const { display: fontDisplay, mono: fontMono, serif: fontSerif } = KHUSHI_FONTS;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const ts = d.getTime();
  if (Number.isNaN(ts)) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
    d.getDate(),
  )}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function datetimeLocalToIso(input: string): string | null {
  if (!input) return null;
  const d = new Date(input);
  const ts = d.getTime();
  if (Number.isNaN(ts)) return null;
  return d.toISOString();
}

export function RevealsTab() {
  const sectionTitle = (text: string) => (
    <Typography
      sx={{
        fontFamily: fontDisplay,
        fontSize: "0.8rem",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: gold,
        mb: 1.5,
      }}
    >
      {text}
    </Typography>
  );

  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [revealSaved, setRevealSaved] = useState(false);
  const [itineraryDoc, setItineraryDoc] = useState<ItineraryConfigDoc | null>(
    null,
  );
  const [revealDraft, setRevealDraft] = useState<Record<string, string>>({});

  const loadReveals = async () => {
    setRevealSaved(false);
    setRevealError(null);
    setRevealLoading(true);
    try {
      const docValue =
        (await fetchItineraryConfig()) ?? buildItineraryConfigDoc(KHUSHI_ITINERARY);
      setItineraryDoc(docValue);
      const draft: Record<string, string> = {};
      for (const id of docValue.order) {
        const item = docValue.itemsById[id];
        if (!item) continue;
        draft[item.id] = isoToDatetimeLocal(item.revealAt);
      }
      setRevealDraft(draft);
    } catch (e: unknown) {
      setRevealError(
        e instanceof Error
          ? e.message
          : "Failed to load itinerary config. Ensure Firestore is enabled and rules allow public reads.",
      );
      setItineraryDoc(null);
      setRevealDraft({});
    } finally {
      setRevealLoading(false);
    }
  };

  const saveReveals = async () => {
    setRevealSaved(false);
    setRevealError(null);
    setRevealLoading(true);
    try {
      const base = itineraryDoc ?? buildItineraryConfigDoc(KHUSHI_ITINERARY);
      const nextItemsById: Record<string, any> = { ...base.itemsById };

      for (const id of base.order) {
        const item = nextItemsById[id];
        if (!item) continue;
        const raw = revealDraft[id] ?? "";
        const iso = datetimeLocalToIso(raw);
        item.revealAt = iso;
      }
      const nextDoc: ItineraryConfigDoc = {
        order: base.order,
        itemsById: nextItemsById,
      };
      await saveItineraryConfig(nextDoc);
      setItineraryDoc(nextDoc);
      setRevealSaved(true);
    } catch (e: unknown) {
      setRevealError(
        e instanceof Error
          ? e.message
          : "Failed to save itinerary config. Ensure your Firestore rules allow owner writes.",
      );
    } finally {
      setRevealLoading(false);
    }
  };

  useEffect(() => {
    if (itineraryDoc === null && !revealLoading) {
      loadReveals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemsForUi = useMemo(
    () =>
      itineraryDoc
        ? itineraryDoc.order.map((id) => itineraryDoc.itemsById[id]).filter(Boolean)
        : KHUSHI_ITINERARY,
    [itineraryDoc],
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          mb: 1.5,
          gap: 1,
        }}
      >
        {sectionTitle("Itinerary reveals")}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => loadReveals()}
            disabled={revealLoading}
            sx={{
              fontFamily: fontDisplay,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderColor: "rgba(212, 175, 55, 0.5)",
              color: goldSoft,
              "&:hover": { borderColor: gold },
            }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => saveReveals()}
            disabled={revealLoading}
            sx={{
              fontFamily: fontDisplay,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              bgcolor: gold,
              color: maroon,
              "&:hover": { bgcolor: goldSoft },
            }}
          >
            Save all
          </Button>
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: fontMono,
          fontSize: "0.75rem",
          color: goldSoft,
          opacity: 0.9,
          mb: 2,
        }}
      >
        Enter times in ET (America/New_York). Saved values are stored as ISO timestamps.
      </Typography>

      {revealError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {revealError}
        </Alert>
      )}
      {revealSaved && !revealError && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved.
        </Alert>
      )}

      {revealLoading && itineraryDoc === null ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} sx={{ color: gold }} />
        </Box>
      ) : (
        <Box>
          {itemsForUi.map((item: any) => {
            const val = revealDraft[item.id] ?? "";
            const iso = datetimeLocalToIso(val);
            const ts = iso ? new Date(iso).getTime() : Number.NaN;
            const status =
              !val || !iso || Number.isNaN(ts)
                ? { label: "Not set", color: "default" as const }
                : ts <= Date.now()
                  ? { label: "Live", color: "success" as const }
                  : { label: "Scheduled", color: "warning" as const };

            return (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { sm: "center" },
                  gap: 1.5,
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid rgba(212, 175, 55, 0.16)",
                  "&:last-of-type": { borderBottom: "none", pb: 0 },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: fontSerif,
                      fontSize: "1.05rem",
                      color: ink,
                      lineHeight: 1.35,
                    }}
                  >
                    {item.timeLabel} — {item.title}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      size="small"
                      label={status.label}
                      color={status.color}
                      sx={{
                        fontFamily: fontDisplay,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                      }}
                    />
                    <Chip
                      size="small"
                      label={item.id}
                      variant="outlined"
                      sx={{
                        fontFamily: fontMono,
                        color: goldSoft,
                        borderColor: "rgba(212, 175, 55, 0.35)",
                      }}
                    />
                  </Box>
                </Box>

                <TextField
                  type="datetime-local"
                  size="small"
                  value={val}
                  onChange={(e) =>
                    setRevealDraft((prev) => ({
                      ...prev,
                      [item.id]:
                        e.target.value.length > 16 ? e.target.value.slice(0, 16) : e.target.value,
                    }))
                  }
                  disabled={revealLoading}
                  sx={{
                    width: { xs: "100%", sm: 260 },
                    "& input": { color: ink, fontFamily: fontMono },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(212, 175, 55, 0.35)",
                    },
                  }}
                  inputProps={{ step: 60 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
}

